import { useEffect, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import './MeetupGroup.css';

const PAGE_SIZE = 30;

function Ratings() {
  const [ratings, setRatings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchRatings = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/ratings?page=${page}&limit=${PAGE_SIZE}`, {
        headers: { [TOKEN_HEADER]: generateToken() }
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to load ratings');
      setRatings(payload.data);
      setPagination(payload.pagination);
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRatings(); }, []);

  const importCSV = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Select a CSV file with name, rating, eventname, and tags columns.');
      return;
    }
    setImporting(true);
    setImportResult(null);
    setError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/ratings/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv', [TOKEN_HEADER]: generateToken() },
        body: await file.text()
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to import ratings');
      setImportResult(payload.data);
      await fetchRatings(1);
    } catch (importError) {
      setError(importError.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="meetup-group" aria-labelledby="ratings-admin-title">
      <div className="meetup-toolbar">
        <div>
          <h2 id="ratings-admin-title">Ratings</h2>
          <p>{pagination.total} imported ratings</p>
        </div>
        <div className="meetup-toolbar-actions">
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={importCSV} hidden />
          <button type="button" disabled={importing} onClick={() => fileInputRef.current?.click()}>
            <Upload size={16} aria-hidden="true" /> {importing ? 'Importing...' : 'Import CSV'}
          </button>
        </div>
      </div>

      {error && <div className="meetup-error" role="alert">{error}</div>}
      {importResult && (
        <div className="meetup-import-result" role="status">
          <strong>Import complete</strong><span>{importResult.inserted} ratings inserted</span>
        </div>
      )}

      <div className="meetup-table-wrap">
        <table className="admin-table meetup-table">
          <thead><tr><th>Name</th><th>Rating</th><th>Event</th><th>Tags</th><th>Imported</th></tr></thead>
          <tbody>
            {!loading && ratings.map((item) => (
              <tr key={item.id}>
                <td><strong>{item.name}</strong></td>
                <td><span aria-label={`${item.rating} out of 5`}>{'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}</span></td>
                <td>{item.event_name}</td>
                <td>{item.tags || '-'}</td>
                <td>{new Date(item.imported_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {!loading && ratings.length === 0 && <tr><td colSpan="5" className="meetup-empty">No ratings imported yet.</td></tr>}
            {loading && <tr><td colSpan="5" className="meetup-empty">Loading ratings...</td></tr>}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="meetup-pagination" aria-label="Ratings pagination">
          <button type="button" disabled={loading || pagination.page <= 1} onClick={() => fetchRatings(pagination.page - 1)}>Previous</button>
          <span>Page {pagination.page} of {pagination.pages}</span>
          <button type="button" disabled={loading || pagination.page >= pagination.pages} onClick={() => fetchRatings(pagination.page + 1)}>Next</button>
        </div>
      )}
    </section>
  );
}

export default Ratings;